// Discount against the standard MRP, shared by the bulk "% off MRP" action and the Discount column of the panel rate grid.

/** The rate after a percentage off the MRP, rounded to a whole rupee (the same rounding the bulk action has always used). */
export const rateFromDiscount = (mrp, pct) => Math.round((Number(mrp) || 0) * (1 - (Number(pct) || 0) / 100));

/** Percentage off MRP that a rate represents, to two decimals. null when there is no MRP to compare with. Negative when the rate is above MRP. */
export const discountPct = (mrp, rate) => {
  const m = Number(mrp);
  if (!Number.isFinite(m) || m <= 0) return null;
  const r = Number(rate);
  if (!Number.isFinite(r)) return null;
  return Math.round((1 - r / m) * 10000) / 100;
};

/** What the Discount column shows for a rate: "20%", "12.5%", "-5%" (above MRP) or "—". */
export const formatDiscount = (mrp, rate) => {
  const p = discountPct(mrp, rate);
  return p === null ? '—' : `${Number.isInteger(p) ? p : p.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
};

/** True when a typed discount can be applied: a number from 0 to 100 with at most two decimals. */
export const isValidDiscount = (text) => {
  // plain digits with at most two decimals: no 1e1, 0x10, spaces or signs
  if (typeof text !== 'string' || !/^\d{1,3}(\.\d{1,2})?$/.test(text)) return false;
  const p = Number(text);
  return p >= 0 && p <= 100;
};

/**
 * The rows a bulk "% off MRP" would change. An item with no MRP is left out: 0 percent of nothing is nothing, and it would
 * overwrite a real rate with 0. Returns the new rows and how many selected items were skipped for that reason.
 */
export const planBulkDiscount = (rows, selectedIds, pct) => {
  const updates = [];
  let skipped = 0;
  (rows || []).forEach((r) => {
    if (!selectedIds.has(r.itemId)) return;
    if (!(Number(r.mrpRate) > 0)) { skipped += 1; return; }
    updates.push({
      itemId: r.itemId,
      rate: Math.max(0, rateFromDiscount(r.mrpRate, pct)),
      mrpRate: r.mrpRate,
      itemDisplayName: r.itemDisplayName || r.investigationName,
      itemCode: r.testCode,
    });
  });
  return { updates, skipped };
};

/** Toast text for a bulk discount. */
export const bulkDiscountMessage = (pct, applied, skipped) =>
  `Applied ${pct}% discount to ${applied} item${applied === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped: no MRP to take a discount from)` : ''}. Click 'Save Rates' to persist.`;

/** The most a single item may be priced at (rupees). The server enforces the same limit. */
export const MAX_PANEL_RATE = 1000000;

/**
 * A typed rate as money: a number from 0 to MAX_PANEL_RATE, rounded to paise. null when it is not usable (empty, text,
 * negative, too large, Infinity), so nothing odd ever reaches the save request.
 */
export const normaliseRate = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  // text must be plain digits with an optional decimal part: no 1e2, 0x10, signs or other scripts' digits
  if (typeof value === 'string' && !/^\d+(\.\d+)?$/.test(value.trim())) return null;
  const n = typeof value === 'number' ? value : Number(value.trim());
  if (!Number.isFinite(n) || n < 0 || n > MAX_PANEL_RATE) return null;
  return Math.round(n * 100) / 100;
};
